import { KeycloakProfile } from "keycloak-js";
import queryString from "query-string";
import React, { useMemo, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { useTheme } from "styled-components";
import {
  AuthorizationVerifier,
  showSection,
  useRoleChecker,
} from "../../components/authorization-verifier";
import { IPropertyChainData, useChainData } from "../../components/chain-data-provider";
import { useLocaleProvider } from "../../components/locale-provider";
import { useLoyaltyBrand } from "../../components/loyalty-brand-provider";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { useUserAuthInfo } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { SECTION, SVGSECTION } from "../../lib/constants";
import { usePermissions } from "../../lib/permissions";
import { AI_USER, CALL_CENTER_LIGHT_USER, CALL_CENTER_USER, EMAIL_ADMIN } from "../../lib/roles";
import { CallCenterLoyalty, MenuItem, MenuItemProps, QuotationMenu } from "./menu-elements";
import { NavMenu } from "./navMenu";

const messages = defineMessages({
  home: {
    id: "menu.home",
    defaultMessage: "Home",
  },
  manuals: {
    id: "menu.manuals",
    defaultMessage: "Manuals",
  },
  reservations: {
    id: "menu.reservations",
    defaultMessage: "Bookings",
  },
  reservationListing: {
    id: "menu.reservations.list",
    defaultMessage: "Listings",
  },
  reservationQuotation: {
    id: "menu.reservations.quotation",
    defaultMessage: "Quote Reservation",
  },
  reservationExpanded: {
    id: "menu.reservations.expandedList",
    defaultMessage: "Expanded List",
  },
  quotationList: {
    id: "menu.reservations.pendingQuotationList",
    defaultMessage: "List of quotations",
  },
  reservationInsert: {
    id: "menu.reservations.insert",
    defaultMessage: "Insert booking",
  },
  insertLoyal: {
    id: "menu.reservations.insertLoyal",
    defaultMessage: "Insert call center booking",
  },
  inventory: {
    id: "menu.inventory",
    defaultMessage: "Inventory",
  },
  inventoryManagement: {
    id: "menu.inventory.management",
    defaultMessage: "Management",
  },
  inventoryExtras: {
    id: "menu.inventory.extras",
    defaultMessage: "Extras / Services",
  },
  inventoryDefine: {
    id: "menu.inventory.define",
    defaultMessage: "Define Inventory",
  },
  inventoryPromotions: {
    id: "menu.inventory.promos",
    defaultMessage: "Promotions / Highlights",
  },
  inventoryDynamicPricing: {
    id: "menu.inventory.dynamicPricing",
    defaultMessage: "Dynamic pricing module",
  },
  integrations: {
    id: "menu.integrations",
    defaultMessage: "Parameterization",
  },
  integrationsGoogleAnalytics: {
    id: "menu.integrations.googleAnalytics",
    defaultMessage: "Google Analytics",
  },
  integrationsMarkupManager: {
    id: "menu.integrations.markupManager",
    defaultMessage: "Markup Manager",
  },
  integrationsCurrencies: {
    id: "menu.integrations.currency",
    defaultMessage: "Currencies",
  },
  integrationsLanguages: {
    id: "menu.integrations.languages",
    defaultMessage: "Languages",
  },
  integrationsOccupancyTypes: {
    id: "menu.integrations.occupancyTypes",
    defaultMessage: "Occupancy Types",
  },
  reports: {
    id: "menu.reports",
    defaultMessage: "Reports",
  },
  reportsBookingStatistics: {
    id: "menu.reports.bookingStatistics",
    defaultMessage: "Booking statistics",
  },
  reportsBookingsByConcept: {
    id: "menu.reports.bookingsByConcept",
    defaultMessage: "Bookings by concept",
  },
  reportsBookingsByState: {
    id: "menu.reports.bookingsByState",
    defaultMessage: "Bookings by status",
  },
  reportsStaysPerDay: {
    id: "menu.reports.staysPerDay",
    defaultMessage: "Stays per day",
  },
  reportsListOfQueries: {
    id: "menu.reports.listOfQueries",
    defaultMessage: "List of queries",
  },
  reportsBillingReport: {
    id: "menu.reports.billingReport",
    defaultMessage: "Billing report",
  },
  reportsWaitingListReport: {
    id: "menu.reports.waitingListReport",
    defaultMessage: "Waiting list report",
  },
  priceFeed: {
    id: "menu.reports.priceFeed",
    defaultMessage: "Price feed",
  },
  communications: {
    id: "menu.communications",
    defaultMessage: "Communications",
  },
  commWebMessages: {
    id: "menu.comm.webMessages",
    defaultMessage: "Web messages",
  },
  commAlerts: {
    id: "menu.comm.alerts",
    defaultMessage: "Alerts",
  },
  commEmailConfirmation: {
    id: "menu.comm.emailConfirmation",
    defaultMessage: "Email confirmation",
  },
  commEmailListing: {
    id: "menu.comm.emailListing",
    defaultMessage: "Listings for emailing",
  },
  commEmailPreStay: {
    id: "menu.comm.emailPreStay",
    defaultMessage: "Email pre-stay",
  },
  commEmailPostStay: {
    id: "menu.comm.emailPostStay",
    defaultMessage: "Email post-stay",
  },
  commPostStaySurvey: {
    id: "menu.comm.postStaySurvey",
    defaultMessage: "Post-stay survey",
  },
  commSurveyResults: {
    id: "menu.comm.surveyResults",
    defaultMessage: "Survey Results (by check-out date)",
  },
  commForms: {
    id: "menu.comm.forms",
    defaultMessage: "Forms",
  },
  email: {
    id: "menu.comm.email",
    defaultMessage: "Email",
  },
  commCustomEmails: {
    id: "menu.comm.customEmails",
    defaultMessage: "Custom emails",
  },
  whatsAppHistory: {
    id: "menu.communications.whatsAppHistory",
    defaultMessage: "WhatsApp History",
  },
  settings: {
    id: "menu.settings",
    defaultMessage: "Settings",
  },
  settingsPropertyInfo: {
    id: "menu.settings.propertyInfo",
    defaultMessage: "Property Info",
  },
  settingsBehavior: {
    id: "menu.settings.behavior",
    defaultMessage: "Behavior",
  },
  settingsParameterization: {
    id: "menu.settings.parameterization",
    defaultMessage: "Parameterization",
  },
  settingsAppearance: {
    id: "menu.settings.appearance",
    defaultMessage: "Appearance",
  },
  settingsDisplay: {
    id: "menu.settings.display",
    defaultMessage: "Display",
  },
  settingsChangePassword: {
    id: "menu.settings.changePassword",
    defaultMessage: "Change Password",
  },
  settingsLegalInfo: {
    id: "menu.settings.legalInfo",
    defaultMessage: "Legal Info",
  },
  settingsModificationPolicies: {
    id: "menu.settings.modificationPolicies",
    defaultMessage: "Modification Policies",
  },
  settingsFilters: {
    id: "menu.settings.filters",
    defaultMessage: "Filters",
  },
  settingsCancellationPolicies: {
    id: "menu.settings.cancellationPolicies",
    defaultMessage: "Cancellation Policies",
  },
  loyalty: {
    id: "menu.loyalty",
    defaultMessage: "Clients",
  },
  loyaltyCustomers: {
    id: "menu.loyalty.customers",
    defaultMessage: "List of clients",
  },
  loyaltyPreferences: {
    id: "menu.loyalty.preferences",
    defaultMessage: "List of preferences",
  },
  derivedLoyaltyTiers: {
    id: "menu.loyalty.derivedTiers",
    defaultMessage: "Loyalty levels",
  },
  constantLoyaltyTiers: {
    id: "menu.loyalty.constantTiers",
    defaultMessage: "Loyaly levels Companies-Agencies",
  },
  customerTransactionReport: {
    id: "menu.loyalty.customerTransactionReport",
    defaultMessage: "Customer transaction report",
  },
  loyaltyOrganizations: {
    id: "menu.loyalty.organizations",
    defaultMessage: "Companies-Agencies",
  },
  b2bCommercialConditions: {
    id: "menu.loyalty.b2bCommercialConditions",
    defaultMessage: "B2B commercial conditions",
  },
  brandManager: {
    id: "menu.loyalty.brandManager",
    defaultMessage: "Brand Manager",
  },
  settingsTagsManager: {
    id: "menu.settings.tagsManager",
    defaultMessage: "Tags management",
  },
  customMapsManager: {
    id: "menu.settings.customMaps",
    defaultMessage: "Mapas personalizados",
  },
  upgradeManager: {
    id: "menu.settings.upgradeManager",
    defaultMessage: "Gestor de Upgrade",
  },
  customerInfoSettings: {
    id: "menu.settings.customerInfoSettings",
    defaultMessage: "Guest data",
  },
  feesManager: {
    id: "menu.settings.feesManager",
    defaultMessage: "Fees",
  },
  taxesManager: {
    id: "menu.settings.taxesManager",
    defaultMessage: "Taxes",
  },
  paymentMethods: {
    id: "menu.settings.paymentMethods",
    defaultMessage: "Payment methods",
  },
  onlineStore: {
    id: "menu.onlineStore",
    defaultMessage: "Online Store",
  },
  onlineStoreOrders: {
    id: "menu.onlineStore.orders",
    defaultMessage: "Orders",
  },
  onlineStoreBookableProducts: {
    id: "menu.onlineStore.bookableProducts",
    defaultMessage: "Bookable Products",
  },
  onlineStoreUnitaryProducts: {
    id: "menu.onlineStore.unitaryProducts",
    defaultMessage: "Unitary Products",
  },
  onlineStorePreview: {
    id: "menu.onlineStore.preview",
    defaultMessage: "Preview store",
  },
  functionalityReport: {
    id: "menu.reports.functionalityReport",
    defaultMessage: "Functionality report",
  },
  orchestrator: {
    id: "menu.orchestrator",
    defaultMessage: "Orchestrator",
  },
  orchestratorActions: {
    id: "menu.orchestrator.globalActions",
    defaultMessage: "Global actions",
  },
  orchestratorSegments: {
    id: "menu.orchestrator.segments",
    defaultMessage: "Segments",
  },
  orchestratorScenarios: {
    id: "menu.orchestrator.scenarios",
    defaultMessage: "Scenarios",
  },
  orchestratorExperiments: {
    id: "menu.orchestrator.experiments",
    defaultMessage: "Experiments",
  },
  orchestratorBookingForm: {
    id: "menu.orchestrator.bookingForm",
    defaultMessage: "Booking form",
  },
  engineFilters: {
    id: "menu.settings.engineFilters",
    defaultMessage: "Filter Management",
  },
  insurances: {
    id: "menu.settings.insurances",
    defaultMessage: "Insurances",
  },
  integrationsCampingMap: {
    id: "menu.integrations.campingMap",
    defaultMessage: "Camping Map",
  },
  whatsAppConcierge: {
    id: "menu.integrations.whatsAppConcierge",
    defaultMessage: "WhatsApp Concierge",
  },
});

interface ClassicMenuProps {
  propertyTicker: string;
  useLegacyExtras?: boolean;
  userProfile?: (KeycloakProfile & { attributes?: Record<string, any> }) | undefined;
}

export const staticURL = "/v6/ng";

export function ClassicMenu({ propertyTicker, userProfile }: ClassicMenuProps) {
  const intl = useIntl();
  const config = useConfig();
  const propertyConfig = usePropertyConfiguration();
  const loyaltyBrand = useLoyaltyBrand();
  const history = useHistory();
  const chainData: IPropertyChainData | undefined | null = useChainData();
  const theme = useTheme();
  const userAuthInfo = useUserAuthInfo();
  const { shortLocale } = useLocaleProvider();
  const isAIUser = useRoleChecker([AI_USER]);

  const isCallCenterUser = useRoleChecker([CALL_CENTER_LIGHT_USER, CALL_CENTER_USER]);
  const isEmailAdmin = useRoleChecker([EMAIL_ADMIN]);
  const showEmailTemplateManager = useMemo(
    () => propertyConfig && propertyConfig?.["showEmailTemplateManager"] === "1" && isEmailAdmin,
    [propertyConfig, isEmailAdmin]
  );
  const showAdvancedEmailsManager = useMemo(
    () => propertyConfig && propertyConfig?.["showAdvancedEmailsManager"] === "1",
    [propertyConfig]
  );
  const showOnlineStore = useMemo(
    () => propertyConfig && propertyConfig?.["onlineStoreActive"] === "1",
    [propertyConfig]
  );
  const showTagManagement = useMemo(() => chainData?.isChain, [chainData]);
  const showQuotation = useMemo(
    () => propertyConfig && propertyConfig?.["isQuotationActive"] === "1",
    [propertyConfig]
  );
  const showOrchestrator = useMemo(() => propertyConfig?.["isOrchestratorActive"] === "1", [
    propertyConfig,
  ]);
  const showOrchestratorTestAb = useMemo(
    () => propertyConfig?.["isOrchestratorTestAbActive"] === "1",
    [propertyConfig]
  );
  const showWhatsAppConcierge = useMemo(
    () => propertyConfig?.["isWhatsAppConciergeActive"] === "1",
    [propertyConfig]
  );

  const showB2bChannel = propertyConfig?.["b2bChannelEnabled"] === "1";

  const userAttributes = useMemo(() => {
    let attributes = {};
    if (userProfile && userProfile.attributes) {
      attributes = Object.entries(userProfile.attributes)
        .map(([key, value]) => [key, Array.isArray(value) && value.length > 0 ? value[0] : ""])
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {} as { [key: string]: string });
    }
    return JSON.stringify(attributes);
  }, [userProfile]);

  const refMenu: any = useRef(null);

  const isSelenta =
    propertyTicker === "sofia" ||
    propertyTicker === "expovalencia" ||
    propertyTicker === "expobarcelona" ||
    propertyTicker === "cleopatrapalace" ||
    propertyTicker === "mediterraneanpalace" ||
    propertyTicker === "hoteldoncarlos" ||
    propertyTicker === "siranthonyhotel" ||
    propertyTicker === "theoasis";

  const insertReservationUrl = queryString.stringifyUrl({
    url: `${config.engineBaseUrl}/${chainData?.isChain ? "chain" : "hotel"}/${propertyTicker}`,
    query: {
      encodedAffiliate: userProfile ? btoa(userProfile.email || "") : undefined,
      origin: isCallCenterUser ? "callcenter" : "backoffice",
      disableAbTest: "1",
      encodedAffiliateAttributes: btoa(userAttributes),
    },
  });

  const onlineStoreCustomDomain = propertyConfig?.["onlineStoreCustomDomain"]?.trim();
  const onlineStorePreviewUrl = useMemo(() => {
    const baseUrl = onlineStoreCustomDomain
      ? (/^https?:\/\//.test(onlineStoreCustomDomain)
          ? onlineStoreCustomDomain
          : `https://${onlineStoreCustomDomain}`
        ).replace(/\/+$/, "")
      : config.onlineStoreUrl;
    return `${baseUrl}/${shortLocale}/${propertyTicker}/products`;
  }, [onlineStoreCustomDomain, config.onlineStoreUrl, shortLocale, propertyTicker]);

  const hideDashboardOption = useMemo(() => chainData?.isChain, [chainData]);
  const permissions = usePermissions();
  const menu = useMemo(
    () =>
      [
        {
          title: intl.formatMessage(messages.reservations),
          section: SECTION.MAIN_RESEREVATIONS,
          mainSection: SECTION.MAIN_RESEREVATIONS,
          routes: ["/reservations"],
          menuItems: [
            {
              message: messages.reservationListing,
              route: `/${propertyTicker}/reservations`,
              section: SECTION.RESERVATIONS_LIST,
              icon: SVGSECTION.reservationListing,
            },
            {
              message: messages.reservationExpanded,
              route: `/${propertyTicker}/reservationsMulti`,
              section: SECTION.RESERVATIONS_LIST_EXTENDED,
              icon: SVGSECTION.reservationExpanded,
            },
            {
              message: messages.quotationList,
              route: `/${propertyTicker}/quotations`,
              section: SECTION.QUOTATION,
              icon: SVGSECTION.reservationExpanded,
              hideItem: !showQuotation,
            },
            {
              message: messages.reservationInsert,
              href: insertReservationUrl,
              section: SECTION.RESERVATIONS_LIST,
              target: "_blank",
              icon: SVGSECTION.reservationInsert,
            },
            {
              hideItem: !loyaltyBrand,
              children: (
                <CallCenterLoyalty
                  message={messages.insertLoyal}
                  propertyTicker={propertyTicker}
                  userProfile={userProfile}
                  engineBaseUrl={config.engineBaseUrl}
                  loyaltyBrand={loyaltyBrand}
                  icon={SVGSECTION.reservationCallCenter}
                />
              ),
            },
            {
              hideItem: !showQuotation,
              section: SECTION.QUOTATION,
              children: (
                <QuotationMenu
                  message={messages.reservationQuotation}
                  propertyTicker={propertyTicker}
                  engineBaseUrl={config.engineBaseUrl}
                  icon={SVGSECTION.reservationInsert}
                  isCallCenterUser={isCallCenterUser}
                  userAttributes={userAttributes}
                  userProfile={userProfile}
                  locale={shortLocale}
                />
              ),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.inventory),
          section: SECTION.MAIN_INVENTORIES,
          mainSection: SECTION.MAIN_INVENTORIES,
          routes: [
            "/ari-management",
            "/extras",
            "/define-inventory",
            "/promotions",
            "/accommodations",
            "/batch-inventory",
            "/inventory-table",
            "/conditions",
            "/mealplan",
            "/inventoryMultiUpdate",
          ],
          menuItems: [
            {
              message: messages.inventoryManagement,
              route: `/${propertyTicker}/ari-management`,
              section: SECTION.INVENTORY_MANAGEMENT,
              icon: SVGSECTION.inventoryManagement,
            },
            {
              message: messages.inventoryExtras,
              route: `/${propertyTicker}/extras`,
              section: SECTION.EXTRAS,
              icon: SVGSECTION.inventoryExtras,
            },
            {
              message: messages.inventoryDefine,
              route: `/${propertyTicker}/define-inventory`,
              section: SECTION.INVENTORY_CONFIGURATION,
              icon: SVGSECTION.inventoryDefine,
            },
            {
              message: messages.inventoryPromotions,
              route: `/${propertyTicker}/promotions`,
              section: SECTION.PROMOTIONS,
              icon: SVGSECTION.inventoryPromotions,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.integrations),
          section: SECTION.MAIN_INTEGRATIONS,
          mainSection: SECTION.MAIN_INTEGRATIONS,
          menuItems: [
            {
              message: messages.integrationsGoogleAnalytics,
              route: `/${propertyTicker}/integrations/analytics-management`,
              section: SECTION.GOOGLE_ANALYTICS,
              icon: SVGSECTION.integrationsGoogleAnalytics,
            },
            {
              message: messages.integrationsLanguages,
              route: `/${propertyTicker}/languages`,
              section: SECTION.LANGUAGE_SETTINGS,
              icon: SVGSECTION.integrationsLanguages,
            },
            {
              message: messages.integrationsCurrencies,
              route: `/${propertyTicker}/currencies`,
              section: SECTION.CURRENCY_SETTINGS,
              icon: SVGSECTION.integrationsCurrencies,
            },
            {
              message: messages.customerInfoSettings,
              route: `/${propertyTicker}/engine-guest-data`,
              section: SECTION.CUSTOMER_INFO_SETTINGS,
              icon: SVGSECTION.customerInfoSettings,
            },
            {
              message: messages.feesManager,
              route: `/${propertyTicker}/fees`,
              section: SECTION.FEES_MANAGER,
              icon: SVGSECTION.feesManager,
            },
            {
              message: messages.taxesManager,
              route: `/${propertyTicker}/taxes`,
              section: SECTION.TAXES_MANAGER,
              icon: SVGSECTION.taxesManager,
            },
            {
              message: messages.paymentMethods,
              route: `/${propertyTicker}/paymentTypes`,
              section: SECTION.PAYMENT_METHODS,
              icon: SVGSECTION.paymentMethods,
            },
            {
              message: messages.integrationsOccupancyTypes,
              route: `/${propertyTicker}/occupancy-types`,
              section: SECTION.OCCUPANT_SETTINGS,
              icon: SVGSECTION.integrationsOccupancyTypes,
            },
            {
              message: messages.insurances,
              route: `/${propertyTicker}/insurances`,
              section: SECTION.INSURANCES,
              icon: SVGSECTION.insurances,
            },
            {
              message: messages.integrationsCampingMap,
              route: `/${propertyTicker}/campingMap`,
              section: SECTION.CAMPING_MAP,
              icon: SVGSECTION.integrationsCampingMap,
            },
            {
              message: messages.whatsAppConcierge,
              route: `/${propertyTicker}/whatsapp-concierge`,
              section: SECTION.MAIN_INTEGRATIONS,
              icon: SVGSECTION.whatsAppConcierge,
              hideItem: !showWhatsAppConcierge || !isAIUser,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.reports),
          section: SECTION.MAIN_REPORTS,
          mainSection: SECTION.MAIN_REPORTS,
          routes: ["/priceFeed"],
          menuItems: [
            {
              message: messages.reportsBookingStatistics,
              route: `/${propertyTicker}/report`,
              section: SECTION.RESERVATION_STATS,
              icon: SVGSECTION.reportsBookingStatistics,
            },
            {
              message: messages.reportsBookingsByConcept,
              route: `/${propertyTicker}/report-concept`,
              section: SECTION.RESERVATION_STATS,
              icon: SVGSECTION.reportsBookingsByConcept,
            },
            {
              message: messages.reportsBookingsByState,
              route: `/${propertyTicker}/report-status`,
              section: SECTION.RESERVATION_STATS,
              icon: SVGSECTION.reportsBookingsByState,
            },
            {
              message: messages.reportsStaysPerDay,
              route: `/${propertyTicker}/report-stays-per-day`,
              section: SECTION.STAYS_PER_DAY,
              icon: SVGSECTION.reportsStaysPerDay,
            },
            {
              message: messages.reportsListOfQueries,
              route: `/${propertyTicker}/report-queries`,
              section: SECTION.QUERY_STATS,
              icon: SVGSECTION.reportsListOfQueries,
            },
            {
              message: messages.reportsWaitingListReport,
              route: `/${propertyTicker}/waiting-list-report`,
              section: SECTION.WAITING_LIST_REPORT,
              icon: SVGSECTION.reportsBillingReport,
            },
            {
              message: messages.priceFeed,
              route: `/${propertyTicker}/priceFeed`,
              section: SECTION.PRICE_FEED,
              icon: SVGSECTION.reportsPriceFeed,
              hideItem: !isSelenta,
            },
            {
              message: messages.functionalityReport,
              route: `/${propertyTicker}/functionality-report`,
              section: SECTION.FUNCTIONALITY_REPORT,
              icon: SVGSECTION.reportsBillingReport,
              hideItem: !userAuthInfo?.isWitAdmin,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.communications),
          section: SECTION.MAIN_COMMUNICATIONS,
          mainSection: SECTION.MAIN_COMMUNICATIONS,
          routes: ["/questionnaire", "emailTemplate"],
          menuItems: [
            {
              message: messages.commWebMessages,
              route: `/${propertyTicker}/web-message`,
              section: SECTION.WEB_MESSAGES,
              icon: SVGSECTION.commWebMessages,
            },
            {
              message: messages.commAlerts,
              route: `/${propertyTicker}/communication-alerts`,
              section: SECTION.ALERT_SETTINGS,
              icon: SVGSECTION.commAlerts,
            },
            {
              message: messages.commForms,
              route: `/${propertyTicker}/questionnaire`,
              section: SECTION.QUESTIONNAIRES,
              icon: SVGSECTION.commForms,
            },
            {
              message: messages.commCustomEmails,
              route: `/${propertyTicker}/emailTemplate`,
              section: SECTION.CUSTOM_EMAIL,
              icon: SVGSECTION.commCustomEmails,
              hideItem: !(showEmailTemplateManager || showAdvancedEmailsManager),
            },
            {
              message: messages.whatsAppHistory,
              route: `/${propertyTicker}/whatsapp-history`,
              section: SECTION.MAIN_COMMUNICATIONS,
              icon: SVGSECTION.whatsAppConcierge,
              hideItem: !showWhatsAppConcierge || !isAIUser,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.settings),
          section: SECTION.MAIN_SETTINGS,
          mainSection: SECTION.MAIN_SETTINGS,
          routes: [
            "/display",
            "/legalInfo",
            "/modification",
            "/filter",
            "/cancellationPolicy",
            "/tags",
            "/customMapsManager",
            "/upgrade-configuration",
          ],
          menuItems: [
            {
              message: messages.settingsPropertyInfo,
              route: `/${propertyTicker}/property-info`,
              section: SECTION.PROPERTY_INFO,
              icon: SVGSECTION.commWebMessages,
            },
            {
              message: messages.settingsBehavior,
              route: `/${propertyTicker}/behaviour`,
              section: SECTION.BEHAVIOR_SETTINGS,
              icon: SVGSECTION.settingsBehavior,
            },
            {
              message: messages.settingsAppearance,
              route: `/${propertyTicker}/engine-appearance`,
              section: SECTION.APPEARANCE_SETTINGS,
              icon: SVGSECTION.settingsAppearance,
            },
            {
              message: messages.settingsDisplay,
              route: `/${propertyTicker}/display`,
              section: SECTION.DISPLAY,
              icon: SVGSECTION.settingsDisplay,
            },
            {
              message: messages.settingsLegalInfo,
              route: `/${propertyTicker}/legalInfo`,
              section: SECTION.LEGAL_INFO,
              icon: SVGSECTION.settingsLegalInfo,
            },
            {
              message: messages.settingsModificationPolicies,
              route: `/${propertyTicker}/modification`,
              section: SECTION.RESERVATION_MODIFICATION_POLICY,
              icon: SVGSECTION.settingsModificationPolicies,
            },
            {
              message: messages.settingsFilters,
              route: `/${propertyTicker}/filter`,
              section: SECTION.FILTERS,
              icon: SVGSECTION.settingsFilters,
            },
            {
              message: messages.engineFilters,
              route: `/${propertyTicker}/engine-filters`,
              section: SECTION.ENGINE_FILTERS,
              icon: SVGSECTION.settingsFilters,
            },
            {
              message: messages.settingsCancellationPolicies,
              route: `/${propertyTicker}/cancellationPolicy`,
              section: SECTION.CANCELLATION_POLICY,
              icon: SVGSECTION.settingsCancellationPolicies,
            },
            {
              message: messages.settingsTagsManager,
              route: `/${propertyTicker}/tags`,
              section: SECTION.TAGS_MANAGER,
              icon: SVGSECTION.tagsManagement,
              hideItem: showTagManagement,
            },
            {
              message: messages.customMapsManager,
              route: `/${propertyTicker}/customMapsManager`,
              section: SECTION.MAPS_MANAGER,
              icon: SVGSECTION.mapsManagement,
              hideItem: !userAuthInfo?.isWitAdmin,
            },
            {
              message: messages.upgradeManager,
              route: `/${propertyTicker}/upgrade-configuration`,
              section: SECTION.UPGRADE_MANAGER,
              icon: SVGSECTION.derivedLoyaltyTiers,
              hideItem: !userAuthInfo?.isWitAdmin,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.loyalty),
          section: SECTION.MAIN_LOYALTY,
          mainSection: SECTION.MAIN_LOYALTY,
          hideMenu: !loyaltyBrand?.ticker,
          routes: [
            "/loyalty-customers",
            "/loyalty-tiers-derived",
            "/loyalty-tiers-constant",
            "/loyalty-customer-transaction-report",
            "/loyalty-organizations",
            "/loyalty-preferences",
            "/brandManager",
          ],
          menuItems: [
            {
              message: messages.loyaltyCustomers,
              route: `/${propertyTicker}/loyalty-customers`,
              section: SECTION.LOYALTY_CUSTOMERS,
              icon: SVGSECTION.loyaltyCustomers,
            },
            {
              message: messages.derivedLoyaltyTiers,
              route: `/${propertyTicker}/loyalty-tiers-derived`,
              section: SECTION.LOYALTY_DERIVED_TIERS,
              icon: SVGSECTION.derivedLoyaltyTiers,
            },
            {
              message: messages.constantLoyaltyTiers,
              route: `/${propertyTicker}/loyalty-tiers-constant`,
              section: SECTION.LOYALTY_CONSTANT_TIERS,
              icon: SVGSECTION.constantLoyaltyTiers,
            },
            {
              message: messages.customerTransactionReport,
              route: `/${propertyTicker}/loyalty-customer-transaction-report`,
              section: SECTION.LOYALTY_CUSTOMER_TRANSACTION_REPORT,
              icon: SVGSECTION.customerTransactionReport,
            },
            {
              message: messages.loyaltyOrganizations,
              route: `/${propertyTicker}/loyalty-organizations`,
              section: SECTION.LOYALTY_ORGANIZATIONS,
              icon: SVGSECTION.loyaltyOrganizations,
            },
            {
              message: messages.b2bCommercialConditions,
              route: `/${propertyTicker}/b2b-conditions`,
              section: SECTION.B2B_COMMERCIAL_CONDITIONS,
              icon: SVGSECTION.loyaltyOrganizations,
              hideItem: !showB2bChannel,
            },
            {
              message: messages.loyaltyPreferences,
              route: `/${propertyTicker}/loyalty-preferences`,
              section: SECTION.LOYALTY_PREFERENCES,
              icon: SVGSECTION.loyaltyPreferences,
            },
            {
              message: messages.brandManager,
              route: `/${propertyTicker}/brandManager`,
              section: SECTION.LOYALTY_BRAND_MANAGER,
              icon: SVGSECTION.inventoryManagement,
              hideItem: !userAuthInfo?.isWitAdmin,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.onlineStore),
          section: SECTION.MAIN_ONLINE_STORE,
          mainSection: SECTION.MAIN_ONLINE_STORE,
          hideMenu: !showOnlineStore,
          routes: ["/store/orders", "/store/bookable-products", "/store/unitary-products"],
          menuItems: [
            {
              message: messages.onlineStoreOrders,
              route: `/${propertyTicker}/store/orders`,
              section: SECTION.ONLINE_STORE_ORDERS,
              icon: SVGSECTION.onlineStoreOrders,
            },
            {
              message: messages.onlineStoreBookableProducts,
              route: `/${propertyTicker}/store/bookable-products`,
              section: SECTION.ONLINE_STORE_BOOKABLE_PRODUCTS,
              icon: SVGSECTION.onlineStoreBookableProducts,
            },
            {
              message: messages.onlineStoreUnitaryProducts,
              route: `/${propertyTicker}/store/unitary-products`,
              section: SECTION.ONLINE_STORE_UNITARY_PRODUCTS,
              icon: SVGSECTION.onlineStoreUnitaryProducts,
            },
            {
              message: messages.onlineStorePreview,
              href: onlineStorePreviewUrl,
              section: SECTION.ONLINE_STORE_ORDERS,
              target: "_blank",
              icon: SVGSECTION.onlineStorePreview,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.orchestrator),
          section: SECTION.MAIN_ORCHESTRATOR,
          mainSection: SECTION.MAIN_ORCHESTRATOR,
          hideMenu: !showOrchestrator,
          routes: [
            "/orchestrator/actions",
            "/orchestrator/segments",
            "/orchestrator/scenarios",
            "/orchestrator/experiments",
          ],
          menuItems: [
            {
              message: messages.orchestratorActions,
              route: `/${propertyTicker}/orchestrator/actions`,
              section: SECTION.ORCHESTRATOR_GLOBAL_ACTIONS,
              icon: SVGSECTION.inventoryManagement,
            },
            {
              message: messages.orchestratorSegments,
              route: `/${propertyTicker}/orchestrator/segments`,
              section: SECTION.ORCHESTRATOR_SEGMENTS,
              icon: SVGSECTION.inventoryManagement,
            },
            {
              message: messages.orchestratorScenarios,
              route: `/${propertyTicker}/orchestrator/scenarios`,
              section: SECTION.ORCHESTRATOR_SCENARIOS,
              icon: SVGSECTION.inventoryManagement,
            },
            {
              message: messages.orchestratorExperiments,
              route: `/${propertyTicker}/orchestrator/experiments`,
              // TODO: update section when experiments has adequate permissions
              section: SECTION.ORCHESTRATOR_EXPERIMENTS,
              icon: SVGSECTION.inventoryManagement,
              hideItem: !showOrchestratorTestAb,
            },
          ],
        },
      ]
        .map(it => ({
          ...it,
          menuItems: (it.menuItems as MenuItemProps[]).filter(mi => {
            const showForced = mi.hideItem === undefined ? true : !Boolean(mi.hideItem);
            if (mi.section === undefined) {
              return showForced;
            }
            const hasPermissions = showSection(permissions, mi.section);
            return hasPermissions && showForced;
          }),
        }))
        .filter(m => m.menuItems.length > 0),
    [
      intl,
      propertyTicker,
      showQuotation,
      insertReservationUrl,
      loyaltyBrand,
      userProfile,
      config.engineBaseUrl,
      isCallCenterUser,
      userAttributes,
      shortLocale,
      showWhatsAppConcierge,
      isAIUser,
      isSelenta,
      userAuthInfo?.isWitAdmin,
      showEmailTemplateManager,
      showAdvancedEmailsManager,
      showTagManagement,
      showOnlineStore,
      onlineStorePreviewUrl,
      showOrchestrator,
      showOrchestratorTestAb,
      permissions,
      showB2bChannel,
    ]
  );

  return (
    <div className="flex h-5" ref={refMenu}>
      <div
        className={`flex gap-[20px] ${
          userAuthInfo?.isWitAdmin && `border-[${theme.textColor}] border-b-[0.5px] pb-3 gap-[20px]`
        }  `}>
        {!hideDashboardOption && (
          <AuthorizationVerifier section={SECTION.DASHBOARD} showForbiddenMessage={false}>
            <NavMenu
              title={intl.formatMessage(messages.home)}
              section={SECTION.DASHBOARD}
              handleClick={() => history.push(`/${propertyTicker}/dashboard`)}
              routes={["/dashboard"]}
            />
          </AuthorizationVerifier>
        )}
        {menu
          .filter(({ hideMenu }) => !hideMenu)
          .map(({ title, section, mainSection, menuItems, routes }, navIndex) => (
            <AuthorizationVerifier
              key={navIndex}
              section={mainSection}
              showForbiddenMessage={false}>
              <NavMenu title={title} section={section} routes={routes}>
                {menuItems && (
                  <ul className="p-[20px] text-sm mb-0" aria-labelledby="dropdownNavbarButton">
                    {menuItems &&
                      (menuItems as any[]).reduce(
                        (acc: any[], item: MenuItemProps, index: number): any => {
                          const {
                            children,
                            hideItem,
                            message,
                            href,
                            icon,
                            target,
                            route,
                            section: sectionChild,
                          } = item;

                          if (!hideItem) {
                            if (children) {
                              return [...acc, <li key={index}>{children}</li>];
                            }
                            acc = [
                              ...acc,
                              <li key={index}>
                                <MenuItem
                                  message={message}
                                  href={href}
                                  section={sectionChild}
                                  icon={icon}
                                  target={target}
                                  route={route}
                                />
                              </li>,
                            ];
                          }
                          return acc;
                        },
                        []
                      )}
                  </ul>
                )}
              </NavMenu>
            </AuthorizationVerifier>
          ))}
      </div>
    </div>
  );
}
